"use client";

import { memo, useId, useMemo } from "react";
import {
  useA2UIComponent,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";
import MarkdownIt from "markdown-it";
import { resolveEnum } from "./utils";

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const HINT_CLASSES: Record<string, string> = {
  h1: "text-2xl font-bold tracking-tight text-foreground",
  h2: "text-xl font-semibold tracking-tight text-foreground",
  h3: "text-lg font-semibold text-foreground",
  h4: "text-base font-semibold text-foreground",
  h5: "text-sm font-semibold text-foreground",
  caption: "text-xs text-muted-foreground",
  body: "text-sm text-foreground leading-relaxed",
};

const PROSE_CLASSES =
  "a2ui-prose prose-sm max-w-none [&_a]:text-primary [&_a]:underline [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground";

export const BeeOSText = memo(function BeeOSText({
  node,
  surfaceId,
}: A2UIComponentProps<Types.TextNode>) {
  const { resolveString } = useA2UIComponent(node, surfaceId);
  const props = node.properties;
  const textValue = resolveString(props.text);
  const usageHint = resolveEnum(props.usageHint, "body");
  const id = useId();

  const html = useMemo(() => {
    if (!textValue) return null;
    return md.render(textValue);
  }, [textValue]);

  if (!html) return null;

  const hostStyle: React.CSSProperties =
    node.weight !== undefined
      ? ({ "--weight": node.weight } as React.CSSProperties)
      : {};

  return (
    <div className="a2ui-text" style={hostStyle}>
      <section
        key={id}
        className={`${PROSE_CLASSES} ${HINT_CLASSES[usageHint] ?? HINT_CLASSES.body}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
});
